import React, { useState, useEffect } from 'react';
import { Link } from '../link';
import { Text } from '../text';
import { Box } from '../layout';
import { useTranslator } from '../custom-hooks/useTranslation';

export const SeeMore = ({
  maxLength = 150,
  text,
  html,
  maxHeight = 20,
  linkProps,
  toViewDetails,
  toRedirect = false,
  isSeeMoreLinkVisible = true,
  fontFamily,
  className,
  color,
  isInline = false,
  ...props
}) => {
  const { t } = useTranslator();
  
  // Generate a unique ID for this instance of SeeMore based on the text content
  const contentId = React.useMemo(() => {
    if (text) {
      return `seemore-${text.substring(0, 20).replace(/\s+/g, '-')}`;
    }
    if (html) {
      return `seemore-html-${html.substring(0, 20).replace(/[<>/\s]+/g, '-')}`;
    }
    return `seemore-${Math.random().toString(36).substring(2, 9)}`;
  }, [text, html]);
  
  // Use localStorage to persist the expanded state
  const [expanded, setExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedState = localStorage.getItem(contentId);
      return savedState === 'true';
    }
    return false;
  });
  
  // Update localStorage when expanded state changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(contentId, expanded.toString());
    }
  }, [expanded, contentId]);
  
  // Check if content needs "see more" button
  const needsSeeMore = (text?.length > maxLength) || (html?.length > maxLength);
  
  // Handle click on "see more/less" button
  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (toRedirect) {
      toViewDetails();
    } else {
      setExpanded(!expanded);
    }
  };
  
  // Function to detect and convert URLs to clickable links
  const convertLinksToHref = (text) => {
    if (!text) return '';
    
    // Regular expression to match URLs
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[^\s]+\.[a-z]{2,}(?:\/[^\s]*)?)/gi;
    
    const parts = text.split(urlRegex);
    
    return parts.map((part, index) => {
      if (urlRegex.test(part)) {
        // Ensure the URL has a protocol
        const href = part.startsWith('http') ? part : `https://${part}`;
        return (
          <a
            key={index}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: '#0066cc',
              textDecoration: 'underline',
              cursor: 'pointer'
            }}
            onClick={(e) => e.stopPropagation()} // Prevent parent click handlers
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };
  
  // Get truncated or full text based on expanded state
  const getDisplayText = () => {
    if (!text) return '';
    if (expanded || !needsSeeMore) return text;
    return text.substring(0, maxLength);
  };
  
  // Function to convert URLs in text to clickable links (matches v4/utils/utils convertLinksInText)
  const convertLinksInText = (text) => {
    if (!text) return '';
    
    // First replace newlines
    let processedText = text
      ?.replace?.('\r', '<br/>')
      ?.replace(/\n/g, '<br/>');
    
    // URL regex pattern: matches http://, https://, www., or plain domains
    const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`[\]]+|www\.[^\s<>"{}|\\^`[\]]+|[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.([a-zA-Z]{2,})(\/[^\s<>"{}|\\^`[\]]*)?)/gi;
    
    // Replace URLs with anchor tags
    processedText = processedText.replace(urlRegex, (url) => {
      // Add protocol if missing (case-insensitive check)
      let href = url;
      const urlLower = url.toLowerCase();
      if (!urlLower.startsWith('http://') && !urlLower.startsWith('https://')) {
        href = `https://${url}`;
      } else {
        // Normalize the protocol to lowercase for href, but keep original display
        href = urlLower.startsWith('http://') ? url : url.replace(/^https?:\/\//i, 'https://');
      }
      
      return `<a href="${href}" target="_blank" rel="noopener noreferrer" style="color: #3182ce; text-decoration: underline;" onclick="event.stopPropagation();">${url}</a>`;
    });
    
    return processedText;
  };


  const stripRedundantBr = (rawHtml) => {
    const blockPattern = 'ol|ul|li|p|div|h[1-6]|blockquote|pre|table|tr|td|th|thead|tbody|tfoot';
    // Pass 1 — remove <br>(s) that come right BEFORE a block tag
    let result = rawHtml.replace(
      new RegExp(`(?:<br\\s*/?>\\s*)+(</?(?:${blockPattern})\\b[^>]*>)`, 'gi'),
      '$1'
    );
    // Pass 2 — remove <br>(s) that come right AFTER a block tag
    result = result.replace(
      new RegExp(`(</?(?:${blockPattern})\\b[^>]*>)\\s*(?:<br\\s*/?>\\s*)+`, 'gi'),
      '$1'
    );
    return result;
  };

  // Get truncated or full HTML based on expanded state
  const getDisplayHtml = () => {
    if (!html) return '';
    const processedHtml = stripRedundantBr(convertLinksInText(html));
    if (expanded || !needsSeeMore) return processedHtml;
    return processedHtml.substring(0, maxLength);
  };
  
  // Render HTML content
  if (html?.length > 0) {
    return (
      <div>
        <Box
          className={className}
          px={2}
          pt={1}
          color='#5B5B5B'
          fontSize={["12px", '16px']}
          fontFamily={fontFamily}
          overflowY="hidden"
          // maxH={expanded ? 'auto' : maxHeight}
          overflowWrap="anywhere"
          dangerouslySetInnerHTML={{ __html: getDisplayHtml() }}
          {...props}
          sx={{
            ul: {
              listStyleType: 'disc',
              listStylePosition: 'outside',
              paddingLeft: '20px',
              marginBottom: '8px',
            },
            ol: {
              listStyleType: 'decimal',
              listStylePosition: 'outside',
              paddingLeft: '20px',
              marginBottom: '8px',
            },
            li: {
              marginBottom: '4px',
            },
            ...props?.sx,
          }}
        />
        
        {needsSeeMore && isSeeMoreLinkVisible && (
          <Text
            px={2}
            pb={2}
            fontSize={["12px", "16px"]}
            fontWeight={400}
            display="inline-block"
            style={{ cursor: 'pointer' }}
            color="brandPrimary.500"
            onClick={handleToggle}
            {...(linkProps ?? {})}
          >
            {expanded 
              ? ` ${t('otherPage.seeless') || '...see less'}` 
              : ` ${t('otherPage.seemore') || '...see more'}`}
          </Text>
        )}
      </div>
    );
  }
  
  // Render inline text with links
  if (isInline) {
    return (
      <Text overflowWrap="anywhere" {...props}>
        {convertLinksToHref(getDisplayText())}
        
        {needsSeeMore && (
          <Text
            as="span"
            style={{ cursor: 'pointer', marginLeft: '4px' }}
            color="brandPrimary.500"
            onClick={handleToggle}
            {...(linkProps ?? {})}
          >
            {expanded 
              ? ` ${t('otherPage.seeless') || 'see less'}` 
              : ` ${t('otherPage.seemore') || '...see more'}`}
          </Text>
        )}
      </Text>
    );
  }
  
  // Render normal text with links
  return (
    <div>
      <Text overflowWrap="anywhere" {...props}>
        {convertLinksToHref(getDisplayText())}
      </Text>
      
      {needsSeeMore && isSeeMoreLinkVisible && (
        <Text
          as="span"
          style={{ cursor: 'pointer', display: 'inline-block' }}
          color="brandPrimary.500"
          onClick={handleToggle}
          {...(linkProps ?? {})}
        >
          {expanded 
            ? ` ${t('otherPage.seeless') || '...see less'}` 
            : ` ${t('otherPage.seemore') || '...see more'}`}
        </Text>
      )}
    </div>
  );
};
